import React, { useState } from 'react';
import { debounce } from 'lodash';
import { Outlet } from 'react-router-dom';
import { Wizard, WizardBody, WizardContent, WizardHeaderLogo, WizardStepper } from '@fiverr-private/navigation';
import { Button } from '@fiverr-private/button';
import { Link } from '@fiverr-private/typography';
import { Box } from '@fiverr-private/theme';
import { logger } from '@fiverr-private/obs';
import pathfinder from '@fiverr-private/pathfinder';
import { ArrowLeftIcon, FiverrLogo } from '@fiverr-private/visuals';
import { Container } from '@fiverr-private/layout_components';
import { getContext } from '@fiverr-private/fiverr_context';
import { ElementType } from '@fiverr-private/bianka_js';
import { useImpressionManager } from '@fiverr-private/impressionable';
import type { ValidationStatus } from '@fiverr-private/store_builder_types';
import { WizardFooter } from '../components/wizard-footer/WizardFooter';
import { WizardHeader } from '../components/wizard-header/WizardHeader';
import { useWizardNavigation } from '../hooks/use-wizard-navigation/useWizardNavigation';
import { useRedirectToLeftOff } from '../hooks/use-redirect-to-left-off/useRedirectToLeftOff';
import { useStoreData } from '../hooks/useStoreData';
import { useSignInModalForGuests } from '../hooks/useSignInModalForGuests';
import { AppI18n } from '../i18n';
import { updateStoreData } from '../services/updateStoreData';
import { useStepValidation } from '../hooks/use-step-validation/useStepValidation';
import { ResetStoreModal } from '../components/reset-store-modal/ResetStoreModal';
import { reportClickEvent } from '../utils/report-bi-event/reportClickEvent';
import { WizardNextBtn } from '../components/wizard-next-btn/WizardNextBtn';
import { shouldSaveStoreData } from '../utils/shouldSaveStoreData';

export const WizardLayout: React.FC = () => {
    useImpressionManager();
    useSignInModalForGuests();
    useRedirectToLeftOff();

    const { queryParameters, userId, pageCtxId } = getContext();
    const [isLoading, setisLoading] = useState(false);
    const { stepValidationStatus, stepValidationOnNextStatus } = useStepValidation();

    const { storeData } = useStoreData();
    const { currentPath, stepperResult, hasPrevStep, hasNextStep, navigateToNextStep, navigateToPreviousStep } =
        useWizardNavigation();

    const homepageUrl = pathfinder('homepage');
    const shouldTriggerResetModal = !!queryParameters?.niche && storeData?.niche?.id !== Number(queryParameters.niche);
    const inProgress = stepValidationStatus(currentPath) === 'inProgress';
    const isCurrentStepValid = stepValidationStatus(currentPath) === 'valid';

    const validateStep = async (): Promise<ValidationStatus> => {
        setisLoading(true);
        const status = await stepValidationOnNextStatus(currentPath);
        setisLoading(false);
        return status;
    };

    const saveStoreData = async (): Promise<void> => {
        if (!storeData || !shouldSaveStoreData(currentPath)) {
            return;
        }

        const updateData = {
            ...(storeData.niche && { niche: storeData.niche }),
            ...(storeData.banners?.length && {
                banners: storeData.banners.map((banner) => ({ id: Number(banner.id) })),
            }),
            ...(storeData.storeUrl && { storeUrl: storeData.storeUrl }),
            ...(storeData.dropshipper && { dropshipper: storeData.dropshipper }),
        };

        try {
            await updateStoreData(storeData.id, updateData);
        } catch (error) {
            logger.error(error as Error, {
                description: 'Failed to update store data',
                store_id: storeData.id,
                page_ctx_id: pageCtxId,
                user_id: userId,
            });
        }
    };

    const handleBackButtonClick = () => {
        navigateToPreviousStep();
    };

    const handleContinueClick = debounce(
        async () => {
            const validationStatus = await validateStep();

            if (validationStatus === 'valid') {
                reportClickEvent({
                    biEventData: {
                        userId,
                        ctxId: pageCtxId,
                    },
                    eventType: 'clicked_on_next',
                    elementType: ElementType.BUTTON,
                    currentPath,
                    store: storeData,
                });

                await saveStoreData();
                navigateToNextStep();
            } else if (validationStatus === 'unauthenticated') {
                window.location.reload();
            }
        },
        2000,
        { leading: true, trailing: false }
    );

    return (
        <>
            <Wizard isOpen={true} useStepperResult={stepperResult}>
                <WizardContent>
                    <WizardHeader>
                        <Button
                            hidden={{
                                default: !hasPrevStep,
                                sm: true,
                            }}
                            justifySelf="flexStart"
                            variant="ghost"
                            onClick={handleBackButtonClick}
                        >
                            <ArrowLeftIcon />
                        </Button>

                        <Container hidden={{ default: true, sm: false }}>
                            <WizardHeaderLogo href={homepageUrl}>
                                <Link href={homepageUrl} decoration="none">
                                    <FiverrLogo size="sm" />
                                </Link>
                            </WizardHeaderLogo>
                        </Container>

                        <Container position="absolute" left="50%" transform="translateX(-50%)">
                            <Box hidden={{ default: true, sm: false }}>
                                <WizardStepper />
                            </Box>

                            <Box hidden={{ default: false, sm: true }}>
                                <WizardHeaderLogo>
                                    <FiverrLogo size="sm" />
                                </WizardHeaderLogo>
                            </Box>
                        </Container>
                    </WizardHeader>

                    <WizardBody display="flex" padding={{ default: '8', sm: '12' }} direction="column">
                        <Outlet />
                    </WizardBody>

                    <WizardFooter>
                        <Box flex="1">
                            <Button
                                hidden={{ default: true, sm: !hasPrevStep }}
                                variant="ghost"
                                onClick={handleBackButtonClick}
                            >
                                <ArrowLeftIcon />
                                <AppI18n k="wizard.back" />
                            </Button>
                        </Box>
                        <Box flex="1" display="flex" justifyContent="flexEnd">
                            <WizardNextBtn
                                disabled={!isCurrentStepValid}
                                hidden={!hasNextStep}
                                onClick={handleContinueClick}
                                isLoading={inProgress || isLoading}
                            />
                            <Button
                                hidden={hasNextStep}
                                href={pathfinder('homepage')}
                                fullWidth={{ sm: false, default: true }}
                            >
                                <AppI18n k="wizard.done" />
                            </Button>
                        </Box>
                    </WizardFooter>
                </WizardContent>
            </Wizard>
            {!!storeData?.userId && <ResetStoreModal shouldOpen={shouldTriggerResetModal} />}
        </>
    );
};

WizardLayout.displayName = 'WizardLayout';
